<template>
  <layout-public>
    <page-meta />

    <page-hero
      :crumbs="[{ label: t('Home'), href: route('index') }, { label: t('Promotions') }]"
      :title="t('Promotions')"
      :lede="t('Every promotion included here carries a fixed price, a written scope and a clear end date, so you always know precisely what you are buying.')"
    />

    <section class="promo-list">
      <div class="container">
        <!-- The pager belongs to the results, so it rides inside them: a list
             with nothing in it has no range to page and the empty message is the
             whole answer. One page still renders the row, so the pager shows for
             a single page too. -->
        <template v-if="cards.length">
          <div class="row gy-4">
            <div v-for="promo in cards" :key="promo.slug" class="col-md-6 col-lg-4" data-aos="fade-up">
              <article class="promo-card">
                <Link :href="route('promotions.show', promo.slug)" class="promo-card-link">
                  <figure class="promo-card-media">
                    <img
                      v-if="promo.image"
                      :src="promo.image"
                      :alt="promo.copy.title"
                      loading="lazy"
                    />
                    <span v-else class="promo-card-placeholder" aria-hidden="true">
                      <font-awesome-icon icon="tag" />
                    </span>

                    <span
                      v-if="promo.status"
                      class="promo-card-status"
                      :class="`promo-card-status--${promo.status}`"
                    >
                      {{ statusLabel(promo.status) }}
                    </span>
                  </figure>

                  <div class="promo-card-body">
                    <h2 class="promo-card-title">{{ promo.copy.title }}</h2>
                    <p class="promo-card-excerpt">{{ promo.copy.excerpt }}</p>

                    <div class="promo-card-foot">
                      <span class="promo-card-meta">
                        <font-awesome-icon icon="circle-info" aria-hidden="true" />
                        {{ promo.copy.meta }}
                      </span>

                      <app-button
                        class="promo-card-action"
                        as="span"
                        variant="link"
                        icon="arrow-right"
                        icon-after
                      >
                        {{ t('View offer') }}
                      </app-button>
                    </div>
                  </div>
                </Link>
              </article>
            </div>
          </div>

          <pagination :links="promotions.links" />
        </template>

        <div v-else class="row">
          <div class="col-12 text-center">
            <p class="text-muted mb-0">{{ t('No promotions right now.') }}</p>
          </div>
        </div>
      </div>
    </section>
  </layout-public>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.promo-list {
  background: $ground-tint;
}

/* One card language with the blog listing (PostCard.vue): white panel, 2px
   blue-tinted border, soft resting shadow, and the same lift on hover and
   keyboard focus. The 2px edge is what keeps the panel visible on this pale
   ground. */
.promo-card {
  background: $ground;
  border: 2px solid $line-strong;
  border-radius: $r-lg;
  box-shadow: $shadow-card;
  height: 100%;
  overflow: hidden;
  transition: border-color $dur $ease, box-shadow $dur $ease, transform $dur $ease;

  &:hover,
  &:focus-within {
    border-color: $ring-focus;
    box-shadow: $shadow-lift;
    transform: translateY(-4px);
  }

  &:focus-within {
    outline: 3px solid $ring-focus;
    outline-offset: 3px;
  }
}

.promo-card-link {
  color: inherit;
  display: flex;
  flex-direction: column;
  height: 100%;
  text-decoration: none;
}

/* Identical media slot to the blog card: fixed 16/10 box, full-bleed cover,
   tinted ground while a lazy image loads. */
.promo-card-media {
  aspect-ratio: 16 / 10;
  background: $ground-accent;
  margin: 0;
  overflow: hidden;
  position: relative;

  img {
    display: block;
    height: 100%;
    object-fit: cover;
    transition: transform $dur-slow $ease;
    width: 100%;
  }
}

.promo-card:hover img,
.promo-card:focus-within img {
  transform: scale(1.05);
}

.promo-card-placeholder {
  align-items: center;
  color: rgba($primary, .4);
  display: flex;
  font-size: 2.4rem;
  height: 100%;
  justify-content: center;
  width: 100%;
}

/* Status pill rides on the cover, in the same slot and shape as the blog date
   badge (PostCard.vue). Ongoing takes the pink accent; expired drops to the
   dark ink so a closed offer never reads as live at a glance. Flex-centred, so
   the label sits dead centre in the pill on both axes. */
.promo-card-status {
  align-items: center;
  background: $secondary;
  border-radius: $r-pill;
  color: $on-dark-strong;
  display: inline-flex;
  font-family: $headings-font-family;
  font-size: 0.68rem;
  font-weight: 600;
  justify-content: center;
  left: 0.9rem;
  letter-spacing: 0.05em;
  padding: 0.4rem 0.75rem;
  position: absolute;
  text-transform: uppercase;
  top: 0.9rem;
}

.promo-card-status--expired {
  background: $ink;
}

.promo-card-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 1.4rem 1.5rem 1.35rem;
}

.promo-card-title {
  color: $text-heading;
  display: -webkit-box;
  font-family: $headings-font-family;
  font-size: 1.2rem;
  line-height: 1.35;
  margin-bottom: 0.6rem;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.promo-card-excerpt {
  color: $text-body;
  display: -webkit-box;
  font-size: 0.92rem;
  line-height: 1.6;
  margin-bottom: 1.25rem;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

/* Pinned to the bottom so the footers line up across a row, whatever the
   title and excerpt run to. */
.promo-card-foot {
  align-items: center;
  border-top: 1px solid $line;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 1rem;
}

.promo-card-meta {
  align-items: center;
  color: $text-muted;
  display: inline-flex;
  flex: 1 1 auto;
  font-size: 0.8rem;
  gap: 0.4rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  svg {
    flex: 0 0 auto;
  }
}

/* The card is the link, so only the layout stays here. The chrome (the
   primary colour, the family and the arrow) is AppButton's `link` variant, so
   "View offer" and "Learn more" cannot drift apart again. Nothing inside the
   button moves on hover; the card's own lift is the feedback. */
.promo-card-action {
  flex: 0 0 auto;
  white-space: nowrap;
}
</style>

<script>
import { Link } from '@inertiajs/inertia-vue3';
import { faArrowRight, faCircleInfo, faTag } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import PageMeta from '@/Components/PageMeta.vue';
import LayoutPublic from '@/Layouts/Public.vue';
import AppButton from '@/Components/AppButton.vue';
import PageHero from '@/Components/PageHero.vue';
import Pagination from '@/Components/Pagination.vue';

FontAwesomeLibrary.add(faArrowRight, faCircleInfo, faTag);

export default {
  components: {
    AppButton,
    PageMeta,
    LayoutPublic,
    Link,
    Pagination,
    PageHero
  },

  props: {
    /* The paginator from PromotionController: `data` is this page's cards and
       `links` is the pager, which is the same shape Blog/Index.vue reads. */
    promotions: {
      type: Object,
      required: true
    }
  },

  computed: {
    cards() {
      return this.promotions.data.map((promo) => ({
        ...promo,
        copy: this.cardCopy(promo.slug)
      }));
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Limited-time offers, each with a set price, a set scope and a clear end date.': 'Limited-time offers, each with a set price, a set scope and a clear end date.',
          'A hand-coded landing page for RM 299, live in about a week. Hosting, your own .vxms.dev address and SSL included, with nothing monthly.': 'A hand-coded landing page for RM 299, live in about a week. Hosting, your own .vxms.dev address and SSL included, with nothing monthly.',
          'From RM 58 a month': 'From RM 58 a month',
          'Every promotion included here carries a fixed price, a written scope and a clear end date, so you always know precisely what you are buying.': 'Every promotion included here carries a fixed price, a written scope and a clear end date, so you always know precisely what you are buying.',
          Expired: 'Expired',
          'No promotions right now.': 'No promotions right now.',
          Ongoing: 'Ongoing',
          'RM 299 once': 'RM 299 once',
          'View offer': 'View offer'
        },
        'zh-MY': {
          'Limited-time offers, each with a set price, a set scope and a clear end date.': '限时优惠，每一档都有固定价格、固定范围与明确的截止日期。',
          'A hand-coded landing page for RM 299, live in about a week. Hosting, your own .vxms.dev address and SSL included, with nothing monthly.': 'RM 299 手写代码落地页，约一周上线。托管、您的 .vxms.dev 地址与 SSL 已包含，之后没有月费。',
          'From RM 58 a month': '每月 RM 58 起',
          'Every promotion included here carries a fixed price, a written scope and a clear end date, so you always know precisely what you are buying.': '这里列出的每一个优惠都有固定的价格、写明的范围与明确的截止日期，让您在付款之前就完全清楚自己买到的是什么，不藏任何额外收费，清楚透明。',
          Expired: '已结束',
          'No promotions right now.': '目前没有优惠。',
          Ongoing: '进行中',
          'RM 299 once': 'RM 299 一次付清',
          'View offer': '查看优惠'
        },
        'ms-MY': {
          'Limited-time offers, each with a set price, a set scope and a clear end date.': 'Tawaran terhad, setiap satu dengan harga tetap, skop tetap dan tarikh tamat yang jelas.',
          'A hand-coded landing page for RM 299, live in about a week. Hosting, your own .vxms.dev address and SSL included, with nothing monthly.': 'Halaman pendaratan dikod tangan untuk RM 299, dalam talian sekitar seminggu. Hosting, alamat .vxms.dev anda dan SSL disertakan, tanpa bayaran bulanan.',
          'From RM 58 a month': 'Dari RM 58 sebulan',
          'Every promotion included here carries a fixed price, a written scope and a clear end date, so you always know precisely what you are buying.': 'Setiap promosi di sini mempunyai harga tetap, skop bertulis dan tarikh tamat yang jelas, jadi anda sentiasa tahu dengan tepat apa yang anda bayar.',
          Expired: 'Tamat Tempoh',
          'No promotions right now.': 'Tiada promosi buat masa ini.',
          Ongoing: 'Sedang Berjalan',
          'RM 299 once': 'RM 299 sekali bayar',
          'View offer': 'Lihat tawaran'
        }
      }
    });

    return { t };
  },

  methods: {
    cardCopy(slug) {
      if (slug === 'rm118-annual-pack') {
        return {
          title: this.t('RM 118 Annual Pack Promo'),
          excerpt: this.t('Pay your web design plan twelve months upfront and pay the annual rate. Startup at RM 58 a month and Growth at RM 118 a month, with the discount on the invoice.'),
          meta: this.t('From RM 58 a month')
        };
      }

      if (slug === 'rm299-landing-page') {
        return {
          title: this.t('RM 299 Landing Page Promo'),
          excerpt: this.t('A hand-coded landing page for RM 299, live in about a week. Hosting, your own .vxms.dev address and SSL included, with nothing monthly.'),
          meta: this.t('RM 299 once')
        };
      }

      return {
        title: slug,
        excerpt: '',
        meta: ''
      };
    },

    statusLabel(status) {
      return this.t(status === 'expired' ? 'Expired' : 'Ongoing');
    }
  }
};
</script>
